<script setup lang="ts">
import Datepicker from '@vuepic/vue-datepicker'
import { format, isEqual, startOfDay } from 'date-fns'
import { useField } from 'vee-validate'
import '@vuepic/vue-datepicker/dist/main.css'
import { mergeRules } from 'core/services/form'
import MonthYear from './monthYear/MonthYear.vue'
import DayCell from './DayCell.vue'

const originalProps = defineProps<{
  autoApply?: boolean
  disableStoryblok?: boolean
  todayTitle?: string
  selectedTitle?: string
  startRangeTitle?: string
  endRangeTitle?: string
  rangeStartDate?: Date
  rangeEndDate?: Date
  minDate?: Date
  maxDate?: Date
  locale?: 'cs' | 'sk'
  disabled?: boolean
  rules?: any
  position?: 'left' | 'right'
  isRangePicker?: boolean
  rangeStep?: 'start' | 'end'
  transitions?: boolean
  modelValue?: Date
  startDate?: Date
  confirmSelectionTitle?: string
  headingTitle?: string
  isRequired?: boolean
  requiredError?: string
  name: string
  label?: string
  tooltip?: any
  placeholder?: string
}>()

const emit = defineEmits(['calendarUpdate', 'internalUpdate', 'monthYearSelect', 'confirmSelection', 'update:modelValue', 'focus'])

const props = originalProps.disableStoryblok ? originalProps : useStoryblokProps(originalProps)
const mergedRules = computed(() => originalProps.disabled ? undefined : mergeRules(originalProps.rules, props.requiredError))

const { value, errorMessage, validate } = useField<string>(props.name, mergedRules, {
  validateOnValueUpdate: false,
})

const datepicker = ref(null)
const isOverlayVisible = ref(false)
const hoveredDate = ref(null)
const DATE_FORMAT = 'd. M. yyyy'

defineExpose({ datepicker })
const ga4 = useAnalytics()

function handleDate(modelData) {
  const date = format(new Date(modelData), 'yyyy-MM-dd\'T\'00:00:00')
  value.value = date
  emit('calendarUpdate', date)
  emit('update:modelValue', date, props.rangeStep)
}

function handleInternalChange(date) {
  if (date === null)
    return

  if (!props.autoApply)
    emit('internalUpdate', date, props.rangeStep)
}

function handleClosePicker() {
  datepicker.value.closeMenu()
}

function isFullRangeSelected(): boolean {
  return !!(props.rangeStartDate && props.rangeEndDate)
}

function getMenuClassName(): string {
  return `${props.isRangePicker ? 'range-datepicker' : ''}
    ${props.rangeStep === 'start' ? 'range-start' : 'range-end'}
    ${props.rangeStartDate ? 'range-has-start' : ''}
    ${props.rangeEndDate ? 'range-has-end' : ''}
    ${isOverlayVisible.value ? 'year-month-overlay__is-visible' : ''}
    ${isEqual(props.rangeStartDate, props.rangeEndDate) ? 'range__date-equal' : ''}
  `
}

function handleToggleOverlay(isVisible: boolean) {
  isOverlayVisible.value = isVisible
}

function handleHoverDate(date: Date | undefined) {
  hoveredDate.value = date ? startOfDay(date) : date
}

function confirmSelection() {
  isFullRangeSelected() && emit('confirmSelection')
}

function handleOnFocus() {
  emit('focus')
  ga4.trackEvent({ event_action: 'datePicker', event_label: originalProps?.name })
}

provide('closePicker', {
  handleClosePicker,
})

provide('toggleOverlay', {
  handleToggleOverlay,
})
watch(errorMessage, () => {
  if (errorMessage.value) ga4.errorEvent({ event_label: originalProps?.name })
})
</script>

<template>
  <div>
    <BaseLabel v-if="!props.isRangePicker" :tooltip="props.tooltip">
      {{ props.label }}
    </BaseLabel>
    <Datepicker
      ref="datepicker"
      data-test-id="datePicker"
      :model-value="value"
      :placeholder="props.placeholder"
      :start-date="props.startDate"
      :locale="props.locale"
      :auto-apply="props.autoApply || true"
      :keep-action-row="!props.autoApply"
      :name="props.name"
      month-name-format="long"
      :month-change-on-scroll="false"
      :enable-time-picker="false"
      :format="DATE_FORMAT"
      :clearable="false"
      :hide-offset-dates="true"
      :max-date="props.maxDate ? startOfDay(props.maxDate) : props.maxDate"
      :min-date="props.minDate ? startOfDay(props.minDate) : props.minDate"
      :position="props.position"
      :menu-class-name="getMenuClassName()"
      :month-year-component="MonthYear as any"
      :calendar-class-name="isFullRangeSelected() ? 'day__range-selected' : null"
      :input-class-name="errorMessage && '!border-error'"
      :custom-props="{
        isRangePicker: props.isRangePicker,
        rangeStep: props.rangeStep,
        startRangeTitle: props.startRangeTitle,
        endRangeTitle: props.endRangeTitle,
        headingTitle: props.headingTitle,
      }"
      :transitions="props.transitions"
      @update:model-value="handleDate"
      @close-picker="handleClosePicker"
      @internal-model-change="handleInternalChange"
      @blur="props.isRequired && validate()"
      @focus="handleOnFocus()"
    >
      <template #input-icon>
        <AppIcon
          class="hover:bg-primary hover:bg-opacity-15 px-[9px] py-[7px] box-content rounded-xl transition-all duration-250 ease-in-out"
          :class="errorMessage && '!text-error'"
          icon="calendar-alt"
          type="far"
          :size="19"
        />
      </template>

      <template #day="{ day, date }">
        <DayCell
          :day="day"
          :date="startOfDay(date)"

          :is-range-picker="props.isRangePicker"
          :range-step="props.rangeStep"
          :range-start-date="props.rangeStartDate"
          :range-end-date="props.rangeEndDate"
          :min-date="props.minDate ? startOfDay(props.minDate) : props.minDate"
          :start-range-title="props.startRangeTitle"
          :end-range-title="props.endRangeTitle"
          :selected-title="props.selectedTitle"
          :is-full-range-selected="isFullRangeSelected()"
          :today-title="props.todayTitle"
          :hovered-date="hoveredDate"
          @hover-date="handleHoverDate"
        />
      </template>

      <template #action-preview>
        <Transition appear name="dp-grow">
          <div v-if="props.rangeStartDate || props.rangeEndDate">
            <p class="pb-2">
              {{ props.selectedTitle }}: {{ props.rangeStartDate && format(props.rangeStartDate, DATE_FORMAT) }} - {{ props.rangeEndDate && format(props.rangeEndDate, DATE_FORMAT) }}
            </p>
          </div>
        </Transition>
      </template>

      <template #action-select>
        <p
          class="cursor-pointer bg-opacity-15 py-2 block transition duration-300 ease-in-out"
          :class="[isFullRangeSelected() ? 'bg-primary font-medium text-primary hover:bg-opacity-30' : 'bg-gray font-normal text-gray-dark']"
          @click="confirmSelection"
        >
          {{ props.confirmSelectionTitle }}
        </p>
      </template>
    </Datepicker>

    <FormFieldErrorMessage v-if="errorMessage">
      {{ errorMessage }}
    </FormFieldErrorMessage>
  </div>
</template>

<style scoped>
:deep(*){
.dp__theme_light {
  --dp-text-color: theme("colors.secondary");
  --dp-hover-color: theme("colors.primary.DEFAULT")26;
  --dp-hover-text-color: theme("colors.secondary");
  --dp-hover-icon-color: theme("colors.primary.DEFAULT");
  --dp-primary-color: theme("colors.primary.DEFAULT");
  --dp-secondary-color: theme("colors.gray.DEFAULT");
  --dp-border-color: theme("colors.gray.DEFAULT");
  --dp-menu-border-color: theme("colors.gray.DEFAULT");
  --dp-border-color-hover: theme("colors.gray.DEFAULT");
  --dp-icon-color: theme("colors.primary.DEFAULT");
}

.dp__instance_calendar {
  @apply pb-5 min-h-[390px] sm:pt-4;
}

.dp__calendar_header_separator,
.dp__arrow_top {
  @apply hidden;
}

.dp__menu {
  @apply <sm:w-310px rounded-xl flex flex-col overflow-hidden shadow-hover;
}

.dp__calendar_header {
  @apply text-gray capitalize font-semibold w-full mb-1;
}

.dp__cell_inner {
  @apply rounded-lg p-0 w-full h-[50px] font-medium items-start overflow-hidden;
}

.dp__calendar {
  @apply px-4;
}

.dp__calendar_item {
  @apply w-[42px];
}

.dp__cell_inner > div {
  @apply w-full;
}

.day__selected {
  @apply opacity-0 text-secondary font-normal text-tiny;
}

.day__today {
  @apply text-tiny;
}

.day__today + .day__selected {
  @apply hidden;
}

.dp__calendar_item {
  @apply relative mt-px;
}

.dp__cell_offset .day__cell-inner {
  background: none;
}

.dp__calendar_item:hover .dp__today:not(.dp__cell_disabled) .day__today + .day__selected,
.dp__active_date.dp__today:not(.dp__cell_disabled) .day__selected {
  @apply inline-block;
}

.dp__calendar_item:hover .day__selected,
.dp__calendar_item:active .day__selected {
  @apply opacity-100;
}

.dp__calendar_item:hover .dp__today:not(.dp__cell_disabled) .day__today,
.dp__calendar_item:active .dp__today:not(.dp__cell_disabled) .day__today,
.dp__active_date .day__today {
  @apply hidden;
}

.dp__active_date .day__selected {
  @apply opacity-100 bg-white;
}

.dp__calendar_row {
  @apply m-0;
}

.dp__calendar_header_item {
  @apply w-[34px] h-[34px];
}

.dp__input_icon {
  @apply right-[5px] left-auto -translate-y-1/2;
}

.dp__input {
  @apply rounded-xl z-2 h-[44px] p-2.5 shadow-form border border-form hover:border-primary;
}

.dp__input_focus,
.dp__input_focus:hover {
  @apply border-primary;
}

.dp__cell_disabled {
  @apply cursor-auto;
}

.dp__cell_disabled .day__selected {
  @apply !opacity-0;
}

.dp__cell_offset .dp__active_date, .dp__cell_offset .dp__range_end, .dp__cell_offset .dp__range_start {
  @apply bg-none;
}

.dp__action_row {
  @apply <sm:border-t <sm:border-t-gray <sm:pt-4 text-gray-dark text-small p-4 pt-0 flex-col;
}

.dp__action_buttons {
  @apply rounded-lg w-full text-center overflow-hidden;
}

.dp__selection_preview {
  @apply w-full text-center sm:hidden;
}

.year-month-overlay__is-visible .dp__action_row {
  @apply hidden;
}

.dp__date_hover:hover {
  @apply bg-primary bg-opacity-15;
}

.dp-grow-enter-active,
.dp-grow-leave-active {
  transition: opacity .3s ease-in-out, max-height .3s ease-in-out;
  max-height: 60px;
}

.dp-grow-enter-from,
.dp-grow-leave-to {
  opacity: 0;
  max-height: 0;
}
}
</style>
